import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface FilterDropdownProps {
  options: string[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  label: string;
  formatLabel?: (value: string) => string;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  label,
  formatLabel
}) => {
  const formatOptionLabel = (value: string) => {
    if (formatLabel) return formatLabel(value);
    // Default formatting: replace underscores with spaces and title case
    return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4, // 4px margin
        left: rect.left
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === 0 || selectedValues.length === options.length) {
      // Currently showing all, so this would filter to show none (but we don't want that)
      // Instead, do nothing or just ensure all are selected
      onChange([]);
    } else {
      // Some are selected, select all
      onChange([...options]);
    }
  };

  const handleToggleOption = (option: string) => {
    if (selectedValues.includes(option)) {
      // Remove from selection
      onChange(selectedValues.filter(v => v !== option));
    } else {
      // Add to selection
      onChange([...selectedValues, option]);
    }
  };

  const isAllSelected = selectedValues.length === 0 || selectedValues.length === options.length;
  const isFiltered = selectedValues.length > 0 && selectedValues.length < options.length;

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 10000,
        minWidth: '200px',
        minHeight: '180px',
        maxHeight: '300px',
        overflowY: 'auto'
      }}
    >
          {/* Select All option */}
          <div
            style={{
              padding: '8px 12px',
              borderBottom: '1px solid #dee2e6',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#f8f9fa'
            }}
            onClick={handleSelectAll}
          >
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              style={{ cursor: 'pointer' }}
              onClick={(e) => e.stopPropagation()}
            />
            <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#212529' }}>All</span>
          </div>

          {/* Individual options */}
          {options.map((option) => (
            <div
              key={option || 'empty'}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'white',
                transition: 'background-color 0.2s'
              }}
              onClick={() => handleToggleOption(option)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => handleToggleOption(option)}
                style={{ cursor: 'pointer' }}
                onClick={(e) => e.stopPropagation()}
              />
              <span style={{ fontSize: '0.9rem', color: '#212529' }}>
                {formatOptionLabel(option) || '(empty)'}
              </span>
            </div>
          ))}

          {options.length === 0 && (
            <div style={{ padding: '12px', textAlign: 'center', color: '#6c757d', fontSize: '0.9rem' }}>
              No options available
            </div>
          )}
    </div>
  );

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px 8px',
          color: isFiltered ? '#FFD700' : 'white',
          fontSize: '0.9rem',
          opacity: isFiltered ? 1 : 0.7
        }}
        title={`Filter ${label}`}
      >
        <i className="fas fa-filter"></i>
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default FilterDropdown;
